import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { Box, Paper, CircularProgress, Chip } from '@mui/material';
import { Button } from '@mui/material';
import { useParams } from 'react-router';
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';
import { Typography } from '@mui/material'; // Fix the import here

import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  BackgroundVariant,
  Connection,
  Node,
  MarkerType
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';
import { useNodeConnect } from './components/hooks/useNodeConnect';
import { useBuildNodeTree } from './components/hooks/useBuildNodeTree';
import SidebarDrawer from './components/sidebar/SidebarDrawer';
import { initialEdges, edgeTypes, nodeTypes, NodeEdge } from './components/data/initialElements';
import { SuccessEdge } from './components/SuccessEdge';
import { FailedEdge } from './components/FailedEdge';
import useApi from 'hooks/useApi';
import DocumentsPanel from '../../../components/DocumentsPanel'; // Adjust the path if needed
import ConditionPopover from './components/ConditionPopover';
import WarningModal from '../../../utils/warningModel/warningMdel';
import { hashDiagram, hashSteps, getChangedSteps } from './components/utils/flowDiff';

/**
 * Autosave switch.
 *
 *  true  — Option B: changes save themselves at each commit point, no Save button, status chip.
 *  false — Option A: manual "Save Flow" button, but with the same data-diff change detection,
 *          so the button only lights up when something genuinely changed.
 *
 * Both paths share one save function and one diff engine; this only picks the trigger and the UI.
 * Flip to false to fall back to manual saving without reverting any code.
 */
const AUTOSAVE_ENABLED = true;

/**
 * Delay between "something changed" and the PUT.
 *
 * This is a coalescing window, not a typing debounce. Form keystrokes never reach this page —
 * Formik keeps them internal and only calls onSubmit through the drawer's imperative save — so
 * the only things that can land here in a burst are node drags. 400ms merges a multi-node drag
 * into a single request while still feeling instant.
 */
const AUTOSAVE_COALESCE_MS = 400;

/** Ceiling on coalescing, so an uninterrupted drag session still commits periodically. */
const AUTOSAVE_MAX_WAIT_MS = 10000;

/** Back-off after a failed save, so a dead network is not hammered every 400ms. */
const AUTOSAVE_RETRY_MS = 5000;

/**
 * How long to wait before re-trying a save that found another save already in flight.
 *
 * Not a back-off — the save is fine, the slot is just busy. Short enough that the change lands as
 * soon as the slot frees, long enough that a multi-second legacy save costs a handful of wakeups
 * rather than a spin.
 */
const AUTOSAVE_INFLIGHT_RETRY_MS = 500;

/** How long to leave "All changes saved" on screen before fading back to nothing. */
const SAVED_CHIP_MS = 2500;

/** How many times the legacy node-adding saver waits a beat for the autosave slot to free up. */
const LEGACY_SAVE_MAX_RETRIES = 10;

// Added support for new no-title edge types in NodeData type
type NodeData = {
  id: string;
  label: string;
  stepNumber?: number;
  onClick: () => void;
  addNodeAndConnect?: (sourceId: string, handleId: 'onCompleteStep' | 'onFailedStep' | 'noTitleEdge1' | 'noTitleEdge2', stepType: string, step: any) => void;
  disabled?: boolean;
  refreshFlow?: () => void;
  step?: any;
  component?: any;
};

export default function QuikFlowPage() {
  const { teamUid } = useParams();
  const hasWorkflowEditPermission = useHasPermission(PERMISSIONS.WORKFLOW_EDIT);
  
  const [flowData, setFlowData] = useState<any>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<NodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [stepTypeMap, setStepTypeMap] = useState({});
  // const nodeTree = useBuildNodeTree(nodes, edges, stepTypeMap);
  const { diagramData, steps } = useBuildNodeTree(nodes, edges, stepTypeMap);
  const [spinerLoading, setSpinerLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [docPanelOpen, setDocPanelOpen] = useState(false);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node<NodeData> | null>(null);
  const [extraNode, setExtraNode] = useState<'status' | 'report' | 'roles' | null>(null);
  const [addStepModalOpen, setAddStepModalOpen] = useState(false);
  const [addStepLoading, setAddStepLoading] = useState(false);
  const [pendingEdgeInfo, setPendingEdgeInfo] = useState<any>(null);
  const [authKeyActive, setAuthKeyActive] = useState<boolean | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [showOutsideClickWarning, setShowOutsideClickWarning] = useState(false);
  const reactFlowWrapperRef = useRef<HTMLDivElement>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [isDrawerReopenDisabled, setIsDrawerReopenDisabled] = useState(false);
  const drawerReopenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [cachedRules, setCachedRules] = useState<any[] | null>(null);
  const [rulesLoading, setRulesLoading] = useState(false);
 

  const addNodeAndConnect = useCallback(useNodeConnect(setNodes, setEdges, setStepTypeMap, setSelectedNode, setDrawerOpen), []);

  const { getFlowByTeamUid, updateFlowDiagram, addStepAndConnect, getRulesByTeamUid } = useApi();

  // ──────────────────────────────────────────────────────────────────────────────
  // Change detection + saving
  //
  // "Is this flow dirty?" is answered by hashing the payload we would send and comparing it
  // against the payload we last successfully sent — never by listening for events. See
  // components/utils/flowDiff.ts for why events were unreliable here.
  // ──────────────────────────────────────────────────────────────────────────────

  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Hashes of the last payload the server accepted. Refs, not state: writing them must never
  // schedule a render on its own, or the save effect would re-run and re-enter flushSave.
  const savedStepHashesRef = useRef<Map<string, string>>(new Map());
  const savedDiagramHashRef = useRef<string | null>(null);

  // Bumped after a baseline is captured, purely to re-render so the derived dirty flag recomputes.
  const [baselineVersion, setBaselineVersion] = useState(0);

  // Guards against two saves overlapping. Shared with the legacy spinerLoading saver below, so
  // the two can never interleave writes to the same step documents.
  const inFlightRef = useRef(false);

  // Pending re-try for a save that found inFlightRef taken. Cleared on unmount.
  const inFlightRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // flushSave calls itself from that re-try timer. Going through a ref keeps flushSave's own
  // dependency list empty, so it keeps the single stable identity the effects below rely on.
  const flushSaveRef = useRef<() => void>(() => {});

  // Re-arms the legacy saver when it finds the slot busy, instead of silently dropping its save.
  const [legacySaveRetry, setLegacySaveRetry] = useState(0);
  const legacySaveRetryRef = useRef(0);

  const stepHashes = useMemo(() => hashSteps(steps as any[]), [steps]);
  const diagramHash = useMemo(() => hashDiagram(diagramData as any[]), [diagramData]);

  // A baseline of null means "not synced with the server yet" — autosave stays off until we know
  // what the server already has, otherwise the first render would look like a flow-wide change.
  const baselineReady = savedDiagramHashRef.current !== null;

  const changedSteps = useMemo(
    () => (baselineReady ? getChangedSteps(steps as any[], savedStepHashesRef.current, stepHashes) : []),
    // baselineVersion is a dependency because savedStepHashesRef is a ref — this is what makes the
    // memo recompute after a baseline capture or a successful save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps, stepHashes, baselineReady, baselineVersion]
  );

  const diagramChanged = baselineReady && savedDiagramHashRef.current !== diagramHash;
  const hasPendingChanges = baselineReady && (changedSteps.length > 0 || diagramChanged);

  // Latest values for callbacks that run from timers/listeners, which would otherwise close over
  // whatever was current when they were created.
  // updateFlowDiagram is in here too, not in a dependency array. Every function on ApiContext is a
  // fresh closure on each ApiProvider render (the silent token refresh re-renders it), so depending
  // on it would give flushSave a new identity constantly — which would restart the coalescing timer
  // forever and, worse, make the unmount-flush effect below tear down and re-run on every one of
  // those renders, firing a save each time.
  const latestRef = useRef<any>({});
  useEffect(() => {
    latestRef.current = {
      steps,
      diagramData,
      stepHashes,
      diagramHash,
      changedSteps,
      diagramChanged,
      teamUid,
      hasWorkflowEditPermission,
      updateFlowDiagram
    };
  });

  /**
   * Record the current canvas as "what the server has".
   *
   * Called after every server read (loadFlow) and after every successful write. Without this,
   * the server's own response would read as a change: it returns populated refs (actor and status
   * objects instead of uids) where our outgoing payload carries uids, so every reload would look
   * like every step had been edited.
   */
  const captureBaseline = useCallback(() => {
    const latest = latestRef.current;
    savedStepHashesRef.current = new Map(latest.stepHashes);
    savedDiagramHashRef.current = latest.diagramHash;
    setBaselineVersion((v) => v + 1);
  }, []);

  const savedChipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Push pending changes to the server.
   *
   * Sends only the steps whose hash moved. `PUT /flow/team/:uid` runs `steps.map(...)` over
   * whatever array it receives and updates those documents individually — it never treats a
   * missing step as a deletion — so a partial array is safe and costs proportionally less.
   *
   * diagramData is the exception and is ALWAYS sent whole: the server stores it with a single
   * `$set` (`diagramData: diagramData ? diagramData : null`), so a partial array would wipe every
   * node position that wasn't included.
   */
  const flushSave = useCallback(async () => {
    const latest = latestRef.current;

    if (!latest.teamUid || !latest.hasWorkflowEditPermission) return;
    if (savedDiagramHashRef.current === null) return; // no baseline yet — nothing to compare against
    if (latest.changedSteps.length === 0 && !latest.diagramChanged) return; // genuinely nothing to do

    // Checked last, and only once we know there is something worth saving: another save (autosave
    // or the legacy node-adding saver) holds the slot, so re-arm rather than return. The coalescing
    // effect below has already fired its timer and only re-runs when one of its dependencies moves,
    // so a plain `return` here left the change pending indefinitely — until the user happened to
    // touch something else, which is what made an edit surface minutes later. The legacy saver
    // holds the slot across its PUT *and* the loadFlow that follows it, so this window is seconds
    // wide, not milliseconds.
    if (inFlightRef.current) {
      if (inFlightRetryRef.current) clearTimeout(inFlightRetryRef.current);
      inFlightRetryRef.current = setTimeout(() => {
        inFlightRetryRef.current = null;
        flushSaveRef.current();
      }, AUTOSAVE_INFLIGHT_RETRY_MS);
      return;
    }

    // Freeze what we are about to send. Anything the user changes while the request is in flight
    // must stay dirty, so the baseline we adopt on success is the payload we sent, not the state
    // of the canvas when the response happens to arrive.
    const sentSteps = latest.changedSteps;
    const sentStepHashes = new Map<string, string>(latest.stepHashes);
    const sentDiagramHash = latest.diagramHash;
    const sentDiagramData = latest.diagramData;

    inFlightRef.current = true;
    setSaveState('saving');

    try {
      const response = await latest.updateFlowDiagram(latest.teamUid, { steps: sentSteps, diagramData: sentDiagramData });

      // `PUT /flow/team/:uid` answers HTTP 200 even when it failed: its catch builds the envelope
      // with `error.statusCode`, which is undefined for a plain runtime error, so the status
      // defaults to 200 and only `success: false` says anything went wrong. Axios resolves, so
      // without this check a rejected save was recorded as saved — the baseline moved, the step's
      // hash matched it forever, and the edit was never sent again.
      if (response && response.success === false) {
        throw new Error(
          `Server rejected the save: ${response.data || response.message || 'unknown error'}`
        );
      }

      savedStepHashesRef.current = sentStepHashes;
      savedDiagramHashRef.current = sentDiagramHash;
      setBaselineVersion((v) => v + 1);
      setSaveState('saved');

      if (savedChipTimeoutRef.current) clearTimeout(savedChipTimeoutRef.current);
      savedChipTimeoutRef.current = setTimeout(() => setSaveState('idle'), SAVED_CHIP_MS);

      // Deliberately no loadFlow() here. Refetching after a save is what used to overwrite edits
      // made while the request was in flight (see the note on the loadRules ref below).
    } catch (error) {
      console.error('Failed to save flow:', error);
      // Baseline untouched, so the change stays dirty and the next tick retries it.
      setSaveState('error');
    } finally {
      inFlightRef.current = false;
    }
    // Empty deps on purpose: everything above is read from latestRef, so this callback keeps one
    // identity for the life of the page and the effects that depend on it never churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // flushSave keeps one identity for the life of the page (empty deps), so assigning once on mount
  // is enough — the same pattern as loadRulesRef below.
  useEffect(() => {
    flushSaveRef.current = flushSave;
    return () => {
      if (savedChipTimeoutRef.current) clearTimeout(savedChipTimeoutRef.current);
      if (inFlightRetryRef.current) clearTimeout(inFlightRetryRef.current);
    };
  }, [flushSave]);

  // Add handler for edge clicks with modal integration
  const handleEdgeClick = useCallback(async (edgeInfo: any) => {
     
    
    // Find parent and child components from nodes
    const parentNode = nodes.find(node => node.id === edgeInfo.parentNodeId);
    const childNode = nodes.find(node => node.id === edgeInfo.childNodeId);
    
    // Find parent and child diagram data (with type casting)
    const parentDiagramData = (diagramData as any[]).find((data: any) => data.id === edgeInfo.parentNodeId);
    const childDiagramData = (diagramData as any[]).find((data: any) => data.id === edgeInfo.childNodeId);
     
    // Combined parent-child diagram data relationship
    if (parentDiagramData && childDiagramData) {
      console.log('Parent-Child Diagram Data Relationship:', {
        parent: {
          id: parentDiagramData.id,
          position: parentDiagramData.position,
          type: parentDiagramData.type,
          measured: parentDiagramData.measured,
          edgeMetadata: parentDiagramData.edgeMetadata,
          component: (parentNode?.data as any)?.component,
          step: (parentNode?.data as any)?.step
        },
        child: {
          id: childDiagramData.id,
          position: childDiagramData.position,
          type: childDiagramData.type,
          measured: childDiagramData.measured,
          edgeMetadata: childDiagramData.edgeMetadata,
          component: (childNode?.data as any)?.component,
          step: (childNode?.data as any)?.step
        },
        connection: {
          edgeId: edgeInfo.edgeId,
          clickPosition: edgeInfo.position,
          edgeData: edgeInfo.edgeData
        }
      });

      // Calculate position for new node (middle of parent and child)
      const newNodePosition = {
        x: (parentDiagramData.position.x + childDiagramData.position.x) / 2,
        y: (parentDiagramData.position.y + childDiagramData.position.y) / 2 + 50 // Offset slightly
      };

      // Create API payload and store it for later use
      const apiPayload = {
        currentNode: {
          title: "New Step Name",
          stepType: "FORM",
          position: newNodePosition,
          parents: [edgeInfo.parentNodeId], // Add parents field
          dependency: [] // Add dependency field
        },
        parentNode: {
          nodeKey: edgeInfo.edgeType as 'onCompleteStep' | 'onFailedStep'
        },
        insertBetween: [
          {
            parentStepUid: edgeInfo.parentNodeId,
            childStepUid: edgeInfo.childNodeId,
            edgeType: edgeInfo.edgeType as 'onCompleteStep' | 'onFailedStep'
          }
        ],
      
        notificationConfig: null
      };
 

      // Store edge info and payload for later use and show modal
      setPendingEdgeInfo({ edgeInfo, apiPayload });
      setAddStepModalOpen(true);
    }
  }, [nodes, diagramData]);

  // Handle adding step after user confirms
  const handleConfirmAddStep = useCallback(async () => {
    if (!pendingEdgeInfo || !teamUid) return;

    setAddStepLoading(true);

    try {
      const response = await addStepAndConnect(teamUid, pendingEdgeInfo.apiPayload);
      console.log('Step added and connected successfully:', response);
      
      // Keep modal open for 1 second to show success state, then close
      setTimeout(() => {
        setAddStepModalOpen(false);
        setAddStepLoading(false);
        setPendingEdgeInfo(null);
        
        // Refresh the flow after modal closes
        loadFlow(teamUid, false);
      }, 1500);
    } catch (error) {
      console.error('Failed to add and connect step:', error);
      // Keep modal open for 1 second to show error state, then close
      setTimeout(() => {
        setAddStepModalOpen(false);
        setAddStepLoading(false);
        setPendingEdgeInfo(null);
      }, 1000);
    }
  }, [pendingEdgeInfo, teamUid, addStepAndConnect]);

  // Handle cancel add step
  const handleCancelAddStep = useCallback(() => {
    // Don't allow cancel if currently loading
    if (addStepLoading) return;
    
    setAddStepModalOpen(false);
    setAddStepLoading(false);
    setPendingEdgeInfo(null);
  }, [addStepLoading]);

  // Create custom edge types with onClick handler for both Success and Failed edges
  const customEdgeTypes = useMemo(() => ({
    ...edgeTypes,
    onCompleteStep: (props: any) => <SuccessEdge {...props} onClick={handleEdgeClick} />,
    onFailedStep: (props: any) => <FailedEdge {...props} onClick={handleEdgeClick} />
  }), [handleEdgeClick]);

  //Adding Node Here
  useEffect(() => {
    if (!spinerLoading) {
      return;
    }
    if (!teamUid) return;

    // setDrawerOpen(true);

    const handler = setTimeout(() => {
      if (loading) {
        setLoading(false);
        setSpinerLoading(false);
        return;
      }

      // inFlightRef is shared with the autosave path so the two savers never overlap and interleave
      // writes to the same step documents. If autosave happens to be mid-request, wait and retry —
      // this save must not be dropped, it is the one that persists a rule-created node.
      if (inFlightRef.current) {
        if (legacySaveRetryRef.current < LEGACY_SAVE_MAX_RETRIES) {
          legacySaveRetryRef.current += 1;
          setLegacySaveRetry((n) => n + 1);
        } else {
          console.warn('Layout save skipped: another save stayed in flight too long');
          legacySaveRetryRef.current = 0;
          setSpinerLoading(false);
        }
        return;
      }

      legacySaveRetryRef.current = 0;

      inFlightRef.current = true;
      // Show saving overlay
      setIsSaving(true);

      // Full payload on purpose: this path runs right after nodes/rules are created server-side,
      // so the local baseline is stale and a diffed payload could miss something.
      //
      // Read from latestRef, not from this effect's closure. The effect only re-runs on
      // spinerLoading/legacySaveRetry, and the PUT fires a second after it did, so the captured
      // `steps`/`diagramData` were a second stale — anything edited in that window was written
      // back at its pre-edit value, and the loadFlow below then pulled that stale value onto the
      // canvas.
      const pending = latestRef.current;
      updateFlowDiagram(teamUid, { steps: pending.steps, diagramData: pending.diagramData })
        .then((response: any) => {
          // Same 200-on-failure envelope as the autosave path: only `success` is trustworthy.
          if (response && response.success === false) {
            throw new Error(
              `Server rejected the layout save: ${response.data || response.message || 'unknown error'}`
            );
          }
          console.log('Layout saved');
          // Reload flow to ensure we have the latest data from backend
          return loadFlow(teamUid, true);
        })
        .catch((error: any) => {
          // Deliberately no loadFlow on failure. Reloading would rebuild the canvas from the
          // server and silently discard the very edits that just failed to save; leaving them on
          // the canvas keeps them dirty so autosave retries them.
          console.error('Failed to save layout:', error);
          setSaveState('error');
        })
        .finally(() => {
          inFlightRef.current = false;
          setSpinerLoading(false);
          // Hide saving overlay
          setIsSaving(false);
        });
    }, 1000);

    return () => clearTimeout(handler);
    // legacySaveRetry re-arms the timer when the slot was busy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinerLoading, legacySaveRetry]);

  const handleUpdateFlow = () => {
    setSpinerLoading(true);
  };

  //Loading Flow by id

  const loadFlow = async (teamUid: string, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await getFlowByTeamUid(teamUid);

      // Same 200-on-failure envelope as the save path. Without this the error string lands in
      // flowData, the rebuild effect bails on `Array.isArray(flowData.steps)` before it reaches
      // setLoading(false), and the page sits on a spinner for good.
      if (response && response.success === false) {
        throw new Error(`Server rejected the load: ${response.data || response.message || 'unknown error'}`);
      }

      setFlowData(response.data);
      setRefreshCounter(prev => prev + 1); // Force useEffect to re-run
      console.log('🔄 Flow data loaded:', response.data);
    } catch (err) {
      console.error('Error loading flow:', err);
      setLoading(false);
    }
  };

  const loadRules = useCallback(async (teamId: string, force = false) => {
    if (!force && (cachedRules || rulesLoading)) return;

    setRulesLoading(true);
    try {
      const response = await getRulesByTeamUid(teamId);
      if (response?.success && Array.isArray(response?.data)) {
        setCachedRules(response.data);
      } else {
        setCachedRules([]);
      }
    } catch (error) {
      console.error('Failed to load rules:', error);
      setCachedRules([]);
    } finally {
      setRulesLoading(false);
    }
  }, [cachedRules, rulesLoading, getRulesByTeamUid]);

  const refreshRules = useCallback(() => {
    if (!teamUid) return;
    loadRules(teamUid, true);
  }, [teamUid, loadRules]);
 ;

  // Keep the latest loadRules in a ref instead of a useEffect dependency.
  // getRulesByTeamUid (and every other ApiContext function) is a fresh
  // closure on every ApiProvider re-render (e.g. the silent 29-minute token
  // refresh), which gives loadRules a new identity each time. If loadRules
  // were a dependency below, that churn would re-fire this effect and call
  // loadFlow() again mid-edit, wiping out unsaved nodes/edges. Reading it via
  // ref means this effect only reacts to an actual teamUid change.
  const loadRulesRef = useRef(loadRules);
  useEffect(() => {
    loadRulesRef.current = loadRules;
  });

  useEffect(() => {
    if (!teamUid) return;
    loadFlow(teamUid);
    loadRulesRef.current(teamUid);

  }, [teamUid]);

  /**
   * Re-baseline after every server read.
   *
   * Keyed on refreshCounter, which only loadFlow increments. That matters: handleStepComponentData
   * also calls setFlowData, and if the baseline reset off flowData itself, a sidebar edit would
   * immediately be recorded as "already saved" and never sent.
   */
  useEffect(() => {
    if (refreshCounter === 0) return;

    // Autosave off while React rebuilds the canvas. The rebuild sets nodes, useBuildNodeTree
    // recomputes steps from them in its own effect, so steps trail nodes by one render — measuring
    // now would capture the previous flow's data as this flow's baseline.
    savedDiagramHashRef.current = null;
    setBaselineVersion((v) => v + 1);

    const settleTimer = setTimeout(() => captureBaseline(), 150);
    return () => clearTimeout(settleTimer);
  }, [refreshCounter, captureBaseline]);

  /**
   * The autosave trigger (Option B). Inert when AUTOSAVE_ENABLED is false — the Save button
   * calls the same flushSave directly in that mode.
   *
   * stepHashes/diagramHash are dependencies so each new change restarts the coalescing window;
   * that is what turns a six-node drag into one request instead of six.
   */
  const firstDirtyAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!AUTOSAVE_ENABLED || !hasWorkflowEditPermission) return;

    if (!hasPendingChanges) {
      firstDirtyAtRef.current = null;
      return;
    }

    if (firstDirtyAtRef.current === null) firstDirtyAtRef.current = Date.now();

    const elapsed = Date.now() - firstDirtyAtRef.current;
    const delay =
      saveState === 'error'
        ? AUTOSAVE_RETRY_MS
        : Math.min(AUTOSAVE_COALESCE_MS, Math.max(0, AUTOSAVE_MAX_WAIT_MS - elapsed));

    const saveTimer = setTimeout(() => flushSave(), delay);
    return () => clearTimeout(saveTimer);
  }, [hasPendingChanges, stepHashes, diagramHash, saveState, hasWorkflowEditPermission, flushSave]);

  /**
   * Commit on the way out: tab hidden, route change, unmount. Covers the gap between the last
   * change and the coalescing window expiring.
   *
   * beforeunload is intentionally not used — a hard tab close cannot reliably finish an axios PUT,
   * and sendBeacon cannot carry the auth headers this API needs. Worst case on a hard kill is the
   * few hundred ms since the last change.
   */
  useEffect(() => {
    if (!AUTOSAVE_ENABLED) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushSave();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      flushSave();
    };
  }, [flushSave]);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setIsDrawerReopenDisabled(true);

    if (drawerReopenTimeoutRef.current) {
      clearTimeout(drawerReopenTimeoutRef.current);
    }

    drawerReopenTimeoutRef.current = setTimeout(() => {
      setIsDrawerReopenDisabled(false);
      drawerReopenTimeoutRef.current = null;
    }, 1500);

    // No explicit save here on purpose. Closing the drawer runs the form's submit, which updates
    // nodes; steps only catch up on the following render (useBuildNodeTree computes in an effect).
    // Flushing now would read stale data, so the coalescing effect picks it up ~400ms later —
    // still imperceptible, and correct.
  }, []);

  useEffect(() => {
    return () => {
      if (drawerReopenTimeoutRef.current) {
        clearTimeout(drawerReopenTimeoutRef.current);
      }
    };
  }, []);

  // Click outside detection.
  // Only meaningful in Option A: it exists to nag about an unsaved flow. With autosave on there is
  // nothing to nag about, and the listener would be a lie, so it is not attached at all.
  useEffect(() => {
    if (AUTOSAVE_ENABLED) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;

      const clickedElement = event.target as Element;

      const ignoreOutsideWarningSelectors = [
        '.MuiPopover-root',
        '.MuiMenu-root',
        '.MuiPopper-root',
        '.MuiAutocomplete-popper',
        '.MuiSelect-select',
        '[role="menu"]',
        '[role="listbox"]',
      ];

      if (ignoreOutsideWarningSelectors.some((selector) => clickedElement.closest(selector))) {
        return;
      }

      if (reactFlowWrapperRef.current && 
          !reactFlowWrapperRef.current.contains(clickedElement)) {
        
        // Always exclude clicks when sidebar is open - check for any drawer-related elements
        if (drawerOpen) {
          // Check if click is inside any MUI Drawer related element
          const clickedElement = event.target as Element;
          
          // Check for MUI Drawer paper
          const drawerPaper = document.querySelector('.MuiDrawer-paper');
          if (drawerPaper && drawerPaper.contains(clickedElement)) {
            return; // Don't show warning if clicking inside drawer
          }
          
          // Check for MUI Backdrop
          const drawerBackdrop = document.querySelector('.MuiBackdrop-root');
          if (drawerBackdrop && drawerBackdrop.contains(clickedElement)) {
            return; // Don't show warning if clicking on backdrop
          }
          
          // Check if the clicked element is part of any MUI component by checking class names
          const elementClasses = clickedElement.className;
          if (typeof elementClasses === 'string' && 
              (elementClasses.includes('Mui') || 
               elementClasses.includes('MuiDrawer') ||
               elementClasses.includes('MuiBackdrop') ||
               elementClasses.includes('MuiPaper'))) {
            return; // Don't show warning for any MUI component clicks
          }
          
          // Check parent elements for MUI classes
          let parentElement = clickedElement.parentElement;
          while (parentElement) {
            const parentClasses = parentElement.className;
            if (typeof parentClasses === 'string' && 
                (parentClasses.includes('MuiDrawer') || 
                 parentClasses.includes('MuiBackdrop') ||
                 parentClasses.includes('MuiPaper'))) {
              return; // Don't show warning if parent is MUI component
            }
            parentElement = parentElement.parentElement;
          }
          
          // If sidebar is open, be more lenient - don't show warning for any UI interaction
          return;
        }
        
        // Only show warning if there are pending changes that need to be saved
        if (hasPendingChanges && !isSaving) {
          setShowOutsideClickWarning(true);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [drawerOpen, hasPendingChanges, isSaving]);

  // ✅ NEW: Apply flowData to nodes + edges if it exists
  useEffect(() => {
    // console.log('🔄 useEffect triggered - refreshCounter:', refreshCounter, 'flowData exists:', !!flowData);
    if (!flowData || !Array.isArray(flowData.steps)) return;
    const { steps, diagramData } = flowData;
    let workflowEdges: NodeEdge[] = []; // Start fresh each time

    const loadedNodes = steps.map((step: any, i: number) => {
      // console.log(step, "stepstepstepstepstepstepstepstepstep");

           // Optimized console logging for isAuthkey access
          steps.forEach((step: any) => {
            if (step.component?.fields) {
              step.component.fields.forEach((field: any) => {
                if (field.settings?.isAuthkey !== undefined) {
                  
                }
              });
            }
          });

          // Check if any field has isAuthkey: true
          const hasAuthKey = steps.some((step: any) =>
            step.component?.fields?.some((field: any) => field.settings?.isAuthkey === true)
          );
          setAuthKeyActive(hasAuthKey);

      // Backend now supports onParallelFlow field for gray button edges
      let { onCompleteStep, onFailedStep, onParallelFlow } = step;

      // Added support for reconstructing edge types from diagramData metadata
      let nodePosition = (diagramData ? diagramData : []).find((dd: any) => dd.id === step.uid);
      let edgeMetadata = nodePosition?.edgeMetadata?.outgoingEdges || [];
      
      

      // Create edges based on stored metadata if available, otherwise fall back to default logic
      if (edgeMetadata.length > 0) {
        edgeMetadata.forEach((edgeMeta: any) => {
          // Skip loop back edges - these will be handled separately by rule-based logic
          if (edgeMeta.type === 'loopBack') {
            // console.log('🚫 Skipping workflow loop edge:', edgeMeta);
            return;
          }
          
          workflowEdges.push({
            id: `e-${step.uid}-${edgeMeta.target}`,
            source: step.uid,
            target: edgeMeta.target,
            type: edgeMeta.type,
            sourceHandle: edgeMeta.sourceHandle,
            animated: true,
            style: { 
              stroke: edgeMeta.type === 'noTitleEdge1' || edgeMeta.type === 'noTitleEdge2' ? '#808080' :
                      edgeMeta.type === 'onFailedStep' ? 'red' : 'green'
            },
            label: edgeMeta.type === 'onCompleteStep' ? 'onComplete' :
                   edgeMeta.type === 'onFailedStep' ? 'onFailed' : '',
            markerEnd: {
              type: MarkerType.Arrow
            }
          });
        });
      } else {
        // Fallback to original logic if no metadata available
        let successEdges = onCompleteStep.map((target: any) => {
          return {
            id: `e-${step.uid}-${target.uid}`,
            source: step.uid,
            target: target.uid,
            type: 'onCompleteStep',
            sourceHandle: 'onCompleteStep',
            animated: true,
            style: { stroke: 'green' },
            label: 'onComplete',
            markerEnd: {
              type: MarkerType.Arrow
            }
          };
        });

        let failedEdges = onFailedStep.map((target: any) => {
          return {
            id: `e-${step.uid}-${target.uid}`,
            source: step.uid,
            target: target.uid,
            type: 'onFailedStep',
            sourceHandle: 'onFailedStep',
            animated: true,
            style: { stroke: 'red' },
            label: 'onFailed',
            markerEnd: {
              type: MarkerType.Arrow
            }
          };
        });

        // Added support for onParallelFlow edges - render as noTitleEdge types with gray color
        let parallelFlowEdges = (onParallelFlow || []).map((target: any, index: number) => {
          return {
            id: `e-${step.uid}-${target.uid}`,
            source: step.uid,
            target: target.uid,
            type: index % 2 === 0 ? 'noTitleEdge1' : 'noTitleEdge2', // Alternate between edge types
            sourceHandle: index % 2 === 0 ? 'noTitleEdge1' : 'noTitleEdge2',
            animated: true,
            style: { stroke: '#808080' },
            label: '', // No label for onParallelFlow edges
            markerEnd: {
              type: MarkerType.Arrow
            }
          };
        });

        workflowEdges = [...workflowEdges, ...successEdges, ...failedEdges, ...parallelFlowEdges];
      }

      let nodeDiagramData = (diagramData ? diagramData : []).find((dd: any) => dd.id === step.uid);

      let nodeConfig = {
        id: step.uid,
        position: { x: 100 + i * 200, y: 100 },
        measured: { height: 100, width: 100 },
        type: 'custom',
        ...(nodeDiagramData ? nodeDiagramData : {})
      };

      return {
        ...nodeConfig,
        data: {
          id: step.uid,
          label: step.title,
          stepNumber: i + 1,
          onClick: () => {
            setSelectedNode({
              ...nodeConfig,
              data: {
                id: step.uid,
                label: step.title,
                stepNumber: i + 1,
                onClick: () => {},
                addNodeAndConnect,
                handleUpdateFlow,
                refreshFlow: () => loadFlow(teamUid!, false),
                step,
                component: step.component,
                Field: step.component.fields,
                disabled: !hasWorkflowEditPermission
              }
            });
            setExtraNode(null);
            setDrawerOpen(true);
          },
          addNodeAndConnect,
          handleUpdateFlow,
          refreshFlow: () => loadFlow(teamUid!, false),
          step,
          component: step.component,
          disabled: !hasWorkflowEditPermission
        }
      };
    });

    const buildLoopEdgesFromRules = (rules: any[]) => {
      const loopEdges: any[] = [];

      if (!Array.isArray(rules) || rules.length === 0) {
        return loopEdges;
      }

      const loopRules = rules.filter((rule: any) => {
        const hasLooping = rule.action?.enableLooping === true;
        const hasLoopBackTo = rule.action?.loopBackTo;

        let sourceStepUid = rule.currentStepUid || rule.currentNodeStepUid;
        if (!sourceStepUid && rule.step) {
          sourceStepUid = typeof rule.step === 'object' ? rule.step.uid : rule.step;
        }

        return hasLooping && hasLoopBackTo && sourceStepUid;
      });

      loopRules.forEach((rule: any, index: number) => {
        let sourceStepUid = rule.currentStepUid || rule.currentNodeStepUid;
        if (!sourceStepUid && rule.step) {
          sourceStepUid = typeof rule.step === 'object' ? rule.step.uid : rule.step;
        }

        const loopBackToUid = typeof rule.action.loopBackTo === 'object'
          ? rule.action.loopBackTo.uid
          : rule.action.loopBackTo;

        const uniqueId = rule._id || `${Date.now()}-${index}`;
        const loopEdgeId = `loop-${sourceStepUid}-${loopBackToUid}-${uniqueId}`;

        loopEdges.push({
          id: loopEdgeId,
          source: sourceStepUid,
          target: loopBackToUid,
          type: 'loopBack',
          sourceHandle: 'loopBackHandle',
          targetHandle: 'loopBackTarget',
          animated: true,
          style: { stroke: '#ff9800' },
          label: '↻ Loop',
          markerEnd: {
            type: MarkerType.Arrow
          },
          data: { ruleId: rule._id, ruleTitle: rule.title }
        });
      });

      return loopEdges;
    };

    const loopEdges = cachedRules ? buildLoopEdgesFromRules(cachedRules) : [];
    const finalEdges = [...workflowEdges, ...loopEdges];

    const edgeIds = finalEdges.map(edge => edge.id);
    const duplicateIds = edgeIds.filter((id, index) => edgeIds.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
      // console.log('⚠️ DUPLICATE EDGE IDs FOUND:', duplicateIds);
    }

    // Keep the position/size a node already has on the canvas. This rebuild re-runs for anything
    // that touches flowData or cachedRules, and the positions it carries are only as fresh as the
    // last server read — the canvas is the authority on layout until a save lands. Without this a
    // rebuild snapped dragged nodes back, and autosave then pushed those reverted positions up,
    // destroying a move that had already been saved.
    setNodes((currentNodes) => {
      if (currentNodes.length === 0) return loadedNodes;

      const currentById = new Map(currentNodes.map((node) => [node.id, node]));

      return loadedNodes.map((node: any) => {
        const existing = currentById.get(node.id);
        if (!existing) return node;

        return {
          ...node,
          position: existing.position ?? node.position,
          measured: existing.measured ?? node.measured
        };
      });
    });
    setEdges(finalEdges);
    setLoading(false);
  }, [flowData, teamUid, refreshCounter, cachedRules]);

  const onConnect = useCallback((params: Connection) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  const handleStepComponentData = (id: string, component: any) => {
  
    
    const componentCopy = { ...component };
    
    let {
      data: { title },
      stepType
    } = componentCopy;
    
    delete componentCopy.data;
    

    let { steps } = flowData;

    steps = steps.map((step: any) => {
      if (step.uid === id) {
        return {
          ...step,
          title,
          ...(stepType ? { stepType } : {}),
          step: {
            ...step.step,
            ...(stepType ? { stepType } : {}),
          },
          component: {
            ...step.component,
            ...componentCopy,
          },
          actor: componentCopy.actor ?? step.actor,
          onComplete: componentCopy.onComplete ?? step.onComplete,
          escalation: componentCopy.escalation ?? step.escalation
        };
      }
      return step;
    });

    // Layout comes from the canvas, never from flowData.diagramData. That field is the snapshot the
    // last server read returned, and dragging a node only ever touches `nodes` — so reusing it here
    // wrote the pre-drag layout back into state, and the rebuild effect above (keyed on flowData)
    // then painted those stale positions over the canvas the moment the drawer closed.
    const liveDiagramData =
      Array.isArray(diagramData) && diagramData.length > 0 ? diagramData : flowData.diagramData;

    let tmpFlowData = { ...flowData, steps, diagramData: liveDiagramData };

    setFlowData(tmpFlowData);
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id !== id) return node;

        return {
          ...node,
          data: {
            ...node.data,
            label: title,
            step: {
              ...node.data.step,
              ...(stepType ? { stepType } : {})
            },
            component: {
              ...node.data.component,
              ...componentCopy
            }
          }
        };
      })
    );
    // No dirty flag to set any more — the step's hash changes on its own if this edit changed
    // anything, and stays identical if the form merely re-submitted the values it was given.

    // setTimeout(() => {
    //   handleUpdateFlow();
    // }, 500);
  };

  /**
   * Manual save. Used by the Save button in Option A (AUTOSAVE_ENABLED = false) and by the
   * "retry" affordance on the failure chip. Same code path as autosave, so both modes send the
   * same partial payload and keep the same baseline.
   */
  const handleSaveFlow = useCallback(() => flushSave(), [flushSave]);

  return (
    <>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', bgcolor: 'background.paper' }}>
         
        <Button 
          variant="outlined" 
          sx={{borderColor:"secondary.dark",color:"text.dark"}} 
          onClick={() => setDocPanelOpen(true)}
          disabled={!hasWorkflowEditPermission}
        >
          Upload/View Documents
        </Button>

        <DocumentsPanel open={docPanelOpen} onClose={() => setDocPanelOpen(false)} entityId={`${flowData?.uid}`} entityType="userflow" />
        <Button
          variant="outlined"
          onClick={() => setRulesDialogOpen(true)}
          sx={{borderColor:"secondary.dark",color:"text.dark",ml:2}}
          disabled={!hasWorkflowEditPermission}
        >
          Rules
        </Button>
        <Button
          variant="outlined"
          
          onClick={() => {
            setDrawerOpen(true);
            setSelectedNode(null);
            setExtraNode('status');
          }}
          sx={{borderColor:"secondary.dark",color:"text.dark",ml:2}}
          disabled={!hasWorkflowEditPermission}
        >
          Status
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setDrawerOpen(true);
            setSelectedNode(null);
            setExtraNode('report');
          }}
          sx={{borderColor:"secondary.dark",color:"text.dark",ml:2}}
          disabled={!hasWorkflowEditPermission}
        >
          Report
        </Button>
      </Box>
      <Box sx={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <SidebarDrawer
            handleStepComponentData={handleStepComponentData}
            open={drawerOpen}
            onClose={handleDrawerClose}
            selectedNode={selectedNode}
            extraNode={extraNode}
            disabled={!hasWorkflowEditPermission}
          />

          <Paper
            elevation={3}
            sx={{
              width: '100%',
              height: '100%',
              mx: 'auto',
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #e0e0e0'
            }}
          >
            <Box sx={{ flexGrow: 1, position: 'relative' }} ref={reactFlowWrapperRef}>
              {loading ? (
                <Box
                  sx={{
                    height: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <CircularProgress size={50} color="primary" />
                </Box>
              ) : (
                <>
                  {/*
                    onNodeDragStop / onConnectStart used to flag the flow dirty by hand, and both
                    were wrong: React Flow fires a drag for a plain click, and a connect drag that
                    is started then abandoned still fires onConnectStart with nothing to save.
                    onNodeDragStop also wrote positions back into flowData, which re-ran the rebuild
                    effect above and recreated every node on every drag.

                    Neither handler is needed now. Positions live on the nodes, useBuildNodeTree
                    derives diagramData from them, and the hash comparison sees a genuine move while
                    ignoring a click or sub-pixel drift.
                  */}
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={hasWorkflowEditPermission ? onNodesChange : () => {}}
                    onEdgesChange={hasWorkflowEditPermission ? onEdgesChange : () => {}}
                    onConnect={hasWorkflowEditPermission ? onConnect : () => {}}
                    onInit={(r) => console.log('flow loaded:', r)}
                    nodeTypes={nodeTypes}
                    edgeTypes={customEdgeTypes}
                    fitView
                    nodesDraggable={hasWorkflowEditPermission}
                    nodesConnectable={hasWorkflowEditPermission}
                    elementsSelectable={true}
                    style={{
                      filter: isSaving ? 'blur(1px)' : 'none',
                      transition: 'filter 0.3s ease-in-out'
                    }}
                  >
                  <Controls />
                  <MiniMap />
                  <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
                  
                  {/* AuthKey status info - positioned on the left */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      zIndex: 1000,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 0.5
                    }}
                  >
                    {authKeyActive !== undefined && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: authKeyActive ? 'green' : 'red',
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }}
                      >
                        Transaction: {authKeyActive ? 'Active' : 'Not Active'}
                      </Typography>
                    )}
                  </Box>

                  {/* Save Flow button - positioned on the right */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      zIndex: 1000,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 0.5
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {AUTOSAVE_ENABLED ? (
                        // Option B: passive status only. Nothing to click, nothing to remember.
                        hasWorkflowEditPermission && (
                          <>
                            {(saveState === 'saving' || hasPendingChanges) && (
                              <Chip
                                size="small"
                                icon={<CircularProgress size={12} thickness={6} />}
                                label="Saving…"
                                sx={{ bgcolor: 'background.paper' }}
                              />
                            )}
                            {saveState === 'saved' && !hasPendingChanges && (
                              <Chip size="small" color="success" variant="outlined" label="All changes saved" sx={{ bgcolor: 'background.paper' }} />
                            )}
                            {saveState === 'error' && (
                              <Chip
                                size="small"
                                color="error"
                                label="Save failed — retrying"
                                onClick={handleSaveFlow}
                                sx={{ bgcolor: 'background.paper' }}
                              />
                            )}
                          </>
                        )
                      ) : (
                        // Option A: manual save, but driven by the same data diff, so the button is
                        // only enabled when something genuinely changed.
                        <>
                          {hasPendingChanges && saveState !== 'saving' && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'warning.main',
                                fontWeight: 'medium',
                                fontSize: '0.75rem',
                                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                padding: '4px 8px',
                                borderRadius: 1
                              }}
                            >
                              Pending changes - Save it!
                            </Typography>
                          )}

                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveFlow}
                            disabled={!hasWorkflowEditPermission || saveState === 'saving' || !hasPendingChanges || isDrawerReopenDisabled}
                          >
                            {saveState === 'saving' ? 'Saving...' : 'Save Flow'}
                          </Button>
                        </>
                      )}
                    </Box>
                    
                    {/* Optional small spinner while saving layout */}
                    {spinerLoading && <CircularProgress size={28} color="primary" />}
                  </Box>
                  </ReactFlow>

                  {/* Saving Overlay */}
                  {isSaving && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 2000,
                        backdropFilter: 'blur(1px)'
                      }}
                    >
                      <CircularProgress 
                        size={40} 
                        color="primary" 
                        sx={{ mb: 2 }}
                      />
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: 'primary.main',
                          fontWeight: 'medium'
                        }}
                      >
                        Saving...
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Rules Dialog */}
      <ConditionPopover
        open={rulesDialogOpen}
        onClose={() => setRulesDialogOpen(false)}
        selectedNode={selectedNode}
        addNodeAndConnect={addNodeAndConnect}
        handleUpdateFlow={handleUpdateFlow}
        refreshFlow={() => teamUid && loadFlow(teamUid, false)}
        refreshRules={refreshRules}
      />

      {/* Add Step Confirmation Modal */}
      <WarningModal
        open={addStepModalOpen}
        onClose={addStepLoading ? () => {} : handleCancelAddStep}
        title="Add New Step"
        type="success"
        heading="Insert New Step"
        message="Are you sure you want to add a new step between the selected workflow nodes? This will create a new step in the workflow and update the connections."
        subHeadings={[
          "• The new step will be positioned between the connected nodes",
          "• Existing workflow connections will be updated",
          "• You can configure the new step after it's created"
        ]}
        confirmText={addStepLoading ? "Adding..." : "Add Step"}
        cancelText="Cancel"
        onConfirm={handleConfirmAddStep}
        onCancel={handleCancelAddStep}
        showCancelButton={!addStepLoading}
        loading={addStepLoading}
        maxWidth="sm"
      />

      {/* Outside Click Warning Modal — Option A only; `open` can never become true when autosave is on. */}
      <WarningModal
        open={!AUTOSAVE_ENABLED && showOutsideClickWarning}
        onClose={() => setShowOutsideClickWarning(false)}
        title="Unsaved Changes"
        type="warning"
        heading="You Have Unsaved Changes!"
        message="You clicked outside the workflow canvas while having unsaved changes. Don't forget to save your work to prevent data loss."
        subHeadings={[
          "• Click 'Save Now' to save your changes automatically",
          "• Your recent modifications are not yet saved",
          "• Avoid navigating away without saving"
        ]}
        confirmText="Save Now"
        onConfirm={async () => {
          setShowOutsideClickWarning(false);
          await handleSaveFlow();
        }}
        showCancelButton={false}
        maxWidth="sm"
      />
    </>
  );
}
